import { useEffect, useState } from "react";
import { BaseUrl } from "../../common/config";
import { http } from "../../helper";
import Header from "../../common/layout/header";
import Sidebar from "../../common/sidebar";
import Footer from "../../common/layout/footer";
import ShowAlert from "../../common/Showalert";
import CommonAlert from "../../common/Commonalert";
import HeroSectionEditor from "./sections/HeroSectionEditor";
import SectorsSectionEditor from "./sections/SectorsSectionEditor";
import WhyPartnerSectionEditor from "./sections/WhyPartnerSectionEditor";
import HowItWorksSectionEditor from "./sections/HowItWorksSectionEditor";
import GrowSectionEditor from "./sections/GrowSectionEditor";
import HomePageBannerEditor from "./sections/HomePageBannerEditor";
import { useTranslation } from "react-i18next";
import { canAction } from "../../common/permissionhelper";
const CMSEditor = ({ pageId = "vendor-landing" }) => {
  const { t } = useTranslation();
  const [vendorLandingContent, setVendorLandingContent] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [alertSuccess, setAlertSuccess] = useState(false);
  const [alertError, setAlertError] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [redirectPath, setRedirectPath] = useState("");
  const [activeTab, setActiveTab] = useState("hero");
  const [tabLoading, setTabLoading] = useState(false);
  const [sectionLoading, setSectionLoading] = useState(null);
  const sectionDisplayNames = {
    hero: t("banner_intro_txt"),
    sectors: t("industry_segments_txt"),
    whyPartner: t("value_proposition_txt"),
    howItWorks: t("how_it_works_txt"),
    grow: t("success_scale_txt"),
    homepagebanner: t("home_page_banner_txt")
  };
  const DEFAULT_HOW_IT_WORKS_STEPS = [
    { stepNumber: 1, title: "", description: "" },
    { stepNumber: 2, title: "", description: "" },
    { stepNumber: 3, title: "", description: "" },
    { stepNumber: 4, title: "", description: "" },
  ];
  // AUTH CHECK
  if (!sessionStorage.getItem("isLoggedIn")) window.location.href = "/";
  const authData = JSON.parse(sessionStorage.getItem("authData"));
  if (
    authData &&
    !authData.adminRole.permissions.includes("manage_foodservicepartners")
  ) {
    window.location.href = "/unauthorized";
  }
  // Fetch content on component mount
  useEffect(() => {
    const fetchContent = async () => {
      setStatus("loading");
      try {
        const response = await http.get(BaseUrl + `admin/cms/${pageId}`);
        setVendorLandingContent(response.data.data);
        setError(null);
      } catch (err) {
        setError(
          err.response?.data?.message || t("failed_fetch_cms_content_txt"),
        );
        console.error("Error fetching CMS content:", err);
      } finally {
        setStatus("idle");
      }
    };
    fetchContent();
  }, [pageId]);
  // Go back to CMS list
  const goBack = () => {
    window.location.href = "/vendorhub/food-service-partner";
  };
  // Save all changes
  const handleSaveAll = async (e) => {
    e.preventDefault();
    if (!vendorLandingContent) return;
    setStatus("updating");
    try {
      const formData = new FormData();
      formData.append("pageId", vendorLandingContent.pageId);
      const sectionsCopy = { ...vendorLandingContent.sections };
      // HERO VIDEO LOGIC
      if (sectionsCopy?.hero) {
        if (sectionsCopy.hero.videoType === "upload") {
          sectionsCopy.hero.videoUrl = "";
        }
        if (sectionsCopy.hero.videoType === "url") {
          sectionsCopy.hero.videoFile = null;
          sectionsCopy.hero.videoFileName = "";
          sectionsCopy.hero.videoFileUrl = "";
        }
        delete sectionsCopy.hero.videoType;
      }
      // THEN handle file extraction
      const videoFile = sectionsCopy?.hero?.videoFile;
      delete sectionsCopy?.hero?.videoFile;
      // ===== HERO IMAGE =====
      const heroImageFile = sectionsCopy?.hero?.imageFile;
      delete sectionsCopy?.hero?.imageFile;
      // ===== WHY PARTNER IMAGE =====
      const whyPartnerImageFile = sectionsCopy?.whyPartner?.imageFile;
      delete sectionsCopy?.whyPartner?.imageFile;
      // ===== GROW IMAGE =====
      const growImageFile = sectionsCopy?.grow?.imageFile;
      delete sectionsCopy.grow.imageFile;
      // ===== HOME PAGE BANNER IMAGE =====
      const homePageBannerImageFile = sectionsCopy?.homepagebanner?.imageFile;
      delete sectionsCopy.homepagebanner.imageFile;
      // ===== BENEFIT ICONS =====
      if (Array.isArray(sectionsCopy?.whyPartner?.benefits)) {
        sectionsCopy.whyPartner.benefits.forEach((b, index) => {
          if (b.iconFile) {
            formData.append("benefitIcons", b.iconFile); // same key
            formData.append("benefitIconIndexes", index); // send index separately
            delete b.iconFile;
          }
        });
      }
      // Append sections JSON
      formData.append("sections", JSON.stringify(sectionsCopy));
      // Append files
      if (videoFile) formData.append("videoFile", videoFile);
      if (heroImageFile) formData.append("heroImage", heroImageFile);
      if (whyPartnerImageFile)
        formData.append("whyPartnerImage", whyPartnerImageFile);
      if (growImageFile) formData.append("growImage", growImageFile);
      if (homePageBannerImageFile) formData.append("homepageBannerImage", homePageBannerImageFile);
      // Send to backend
      const response =
        vendorLandingContent._isNew || !vendorLandingContent._id
          ? await http.post(`${BaseUrl}admin/cms`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
          : await http.put(`${BaseUrl}admin/cms/${pageId}`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
      // ALWAYS REFRESH STATE FROM SERVER
      const updated = await http.get(BaseUrl + `admin/cms/${pageId}`);
      setVendorLandingContent(updated.data.data);
      setAlertSuccess(true);
      setAlertMessage(t("sections_saved_success_txt"));
    } catch (err) {
      setAlertError(true);
      setAlertMessage(
        err.response?.data?.message || t("failed_save_cms_content_txt"),
      );
      console.error(err);
    } finally {
      setStatus("idle");
    }
  };
  // Handle updates from section editors
  const handleSectionChange = (sectionName, updatedData) => {
    setVendorLandingContent((prev) => ({
      ...(prev || { pageId }),
      sections: {
        ...(prev?.sections || {}),
        [sectionName]: updatedData,
      },
    }));
  };
  // Create new content structure if no content exists
  const createNewContent = () => {
    setVendorLandingContent({
      pageId,
      sections: {
        hero: {
          title: "",
          subtitle: "",
          ctaText: "",
          ctaLink: "",
          videoUrl: "",
          videoFile: null,
          videoFileName: "",
        },
        sectors: { title: "", description: "" },
        whyPartner: { title: "", description: "", image: "", benefits: [] },
        howItWorks: { title: "", description: "", steps: DEFAULT_HOW_IT_WORKS_STEPS, },
        grow: { title: "", description: "", image: "" },
        homepagebanner: { description: "", buttonText:"", image:""}
      },
      // flag to indicate this record doesn't exist on server yet
      _isNew: true,
    });
  };
  // Handle tab change with loading state
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    // CLEAR ALERT STATE
    setAlertSuccess(false);
    setAlertError(false);
    setAlertMessage("");
    setTabLoading(true);
    setTimeout(() => {
      setActiveTab(tab);
      setTabLoading(false);
    }, 150); // small delay for smooth UX
  };
  const handleSaveSection = async (sectionName, sectionData) => {
    try {
      setSectionLoading(sectionName);
      const formData = new FormData();
      const sectionCopy = { ...sectionData };
      // ===== HERO FILES =====
      if (sectionName === "hero") {
        if (sectionCopy.videoType === "upload") {
          // FORCE CLEAR URL
          formData.append("videoUrl", "");
          delete sectionCopy.videoUrl;
        }
        if (sectionCopy.videoType === "url") {
          // FORCE CLEAR FILE
          formData.append("videoFileUrl", "");
          delete sectionCopy.videoFile;
          delete sectionCopy.videoFileName;
          delete sectionCopy.videoFileUrl;
        }
        if (sectionCopy.videoFile) {
          formData.append("videoFile", sectionCopy.videoFile);
          delete sectionCopy.videoFile;
        }
        delete sectionCopy.videoType;
      }
      // ===== WHY PARTNER FILES =====
      if (sectionName === "whyPartner") {
        if (sectionCopy.imageFile) {
          formData.append("whyPartnerImage", sectionCopy.imageFile);
          delete sectionCopy.imageFile;
        }
        if (Array.isArray(sectionCopy.benefits)) {
          sectionCopy.benefits.forEach((b, index) => {
            if (b.iconFile) {
              formData.append("benefitIcons", b.iconFile); // same field name
              formData.append("benefitIconIndexes", index); // mapping index
              delete b.iconFile;
            }
          });
        }
      }
      // ===== GROW IMAGE FILES =====
      if (sectionName === "grow") {
        if (sectionCopy.imageFile) {
          formData.append("growImage", sectionCopy.imageFile);
          delete sectionCopy.imageFile;
        }
      }
      if (sectionName === "homepagebanner") {
        if (sectionCopy.imageFile) {
          formData.append("homepageBannerImage", sectionCopy.imageFile);
          delete sectionCopy.imageFile;
        }
      }
      // HANDLE ARRAYS
      Object.keys(sectionCopy).forEach((key) => {
        const value = sectionCopy[key];
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      await http.put(
        `${BaseUrl}admin/cms/${pageId}/section/${sectionName}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const updated = await http.get(BaseUrl + `admin/cms/${pageId}`);
      setVendorLandingContent(updated.data.data);
      setAlertSuccess(true);
      const displayName = sectionDisplayNames[sectionName] || sectionName;
      setAlertMessage(`${displayName} section saved successfully!`);
    } catch (err) {
      setAlertError(true);
      setAlertMessage(
        err.response?.data?.message || t("failed_save_section_txt"),
      );
    } finally {
      setSectionLoading(null);
    }
  };
  // Rendering logic
  return (
    <section className="dashboard-block">
      {alertSuccess && (
        <CommonAlert
          title={t("alert_txt")}
          message={alertMessage}
          redirectTo={redirectPath}
          onClose={() => {
            setAlertSuccess(false);
            setAlertMessage("");
          }}
        />
      )}
      {alertError && (
        <ShowAlert title={t("error_txt")} message={alertMessage} />
      )}
      <Sidebar />
      <div className="dash-left">
        <Header />
        <section className="cart-block">
          <div className="container food-partner-cms">
            <div className="flex-title">
              <h1 className="checkout-title">
                {t("vendor_landing_cms_editor_title_txt")}
              </h1>
              <div className="prod-import-btn odr-expo-btn">
                {canAction('manage_foodservicepartners', 'edit') && (
                <a
                  className="export-btn button"
                  onClick={handleSaveAll}
                  style={{ cursor: "pointer" }}
                >
                  {status === "updating"
                    ? t("saving_txt")
                    : t("save_all_sections_txt")}
                </a>
                )}
              </div>
            </div>
            <div className="right-table-outer-wrap">
              {status === "loading" ? (
                <div className="loading-buffer"></div>
              ) : !vendorLandingContent ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <p
                    style={{
                      fontSize: "16px",
                      marginBottom: "20px",
                      color: "#666",
                    }}
                  >
                    {t("no_content_found_txt")}
                  </p>
                  <button
                    onClick={createNewContent}
                    style={{
                      padding: "12px 30px",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("create_new_content_txt")}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="header-tab-menu">
                    <a
                      className={activeTab === "hero" ? "active" : ""}
                      onClick={() => handleTabChange("hero")}
                    >
                      {t("banner_intro_txt")}
                    </a>
                    <a
                      className={activeTab === "sectors" ? "active" : ""}
                      onClick={() => handleTabChange("sectors")}
                    >
                      {t("industry_segments_txt")}
                    </a>
                    <a
                      className={activeTab === "whyPartner" ? "active" : ""}
                      onClick={() => handleTabChange("whyPartner")}
                    >
                      {t("value_proposition_txt")}
                    </a>
                    <a
                      className={activeTab === "howItWorks" ? "active" : ""}
                      onClick={() => handleTabChange("howItWorks")}
                    >
                      {t("how_it_works_txt")}
                    </a>
                    <a
                      className={activeTab === "grow" ? "active" : ""}
                      onClick={() => handleTabChange("grow")}
                    >
                      {t("success_scale_txt")}
                    </a>
                    <a
                      className={activeTab === "homepagebanner" ? "active" : ""}
                      onClick={() => handleTabChange("homepagebanner")}
                    >
                      {t("home_page_banner_txt")}
                    </a>
                  </div>
                  <form className="sectors-detail-form">
                    {tabLoading ? (
                      <div
                        className="loading-buffer"
                        style={{ minHeight: "200px" }}
                      ></div>
                    ) : (
                      <>
                        {activeTab === "hero" && (
                          <HeroSectionEditor
                            section={vendorLandingContent.sections?.hero}
                            onUpdate={(data) =>
                              handleSectionChange("hero", data)
                            }
                            onSave={handleSaveSection}
                            isSaving={sectionLoading === "hero"}
                          />
                        )}
                        {activeTab === "sectors" && (
                          <SectorsSectionEditor
                            section={vendorLandingContent.sections?.sectors}
                            onUpdate={(data) =>
                              handleSectionChange("sectors", data)
                            }
                            onSave={handleSaveSection}
                            isSaving={sectionLoading === "sectors"}
                          />
                        )}
                        {activeTab === "whyPartner" && (
                          <WhyPartnerSectionEditor
                            section={vendorLandingContent.sections?.whyPartner}
                            onUpdate={(data) =>
                              handleSectionChange("whyPartner", data)
                            }
                            onSave={handleSaveSection}
                            isSaving={sectionLoading === "whyPartner"}
                          />
                        )}
                        {activeTab === "howItWorks" && (
                          <HowItWorksSectionEditor
                            section={vendorLandingContent.sections?.howItWorks}
                            onUpdate={(data) =>
                              handleSectionChange("howItWorks", data)
                            }
                            onSave={handleSaveSection}
                            isSaving={sectionLoading === "howItWorks"}
                          />
                        )}
                        {activeTab === "grow" && (
                          <GrowSectionEditor
                            section={vendorLandingContent.sections?.grow}
                            onUpdate={(data) =>
                              handleSectionChange("grow", data)
                            }
                            onSave={handleSaveSection}
                            isSaving={sectionLoading === "grow"}
                          />
                        )}
                        {activeTab === "homepagebanner" && (
                          <HomePageBannerEditor
                            section={vendorLandingContent.sections?.homepagebanner}
                            onUpdate={(data) =>
                              handleSectionChange("homepagebanner", data)
                            }
                            onSave={handleSaveSection}
                            isSaving={sectionLoading === "homepagebanner"}
                          />
                        )}
                      </>
                    )}
                  </form>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </section>
      </div>
    </section>
  );
};

export default CMSEditor;